import { useMemo } from 'react';
import { Accordion } from '@interxfi/design-system/components/accordion/accordion';
import AvatarIcon from '@interxfi/design-system/icons/react/icon-avatar';
import BankIcon from '@interxfi/design-system/icons/react/icon-bank';
import BoltIcon from '@interxfi/design-system/icons/react/icon-bolt';
import DevelopersIcon from '@interxfi/design-system/icons/react/icon-developers';
import LedgerIcon from '@interxfi/design-system/icons/react/icon-ledger';
import OrdersIcon from '@interxfi/design-system/icons/react/icon-orders';
import NumberIcon from '@interxfi/design-system/icons/react/icon-number';
import type {
  IxcFundingV1AccountNumbersControls,
  IxcFundingV1BatchFundingControls,
  IxcFundingV1FundingControls,
  IxcOrdersV1BatchOrderControls,
  IxcOrdersV1OrderControls,
  IxcPortfolioV1BooksControls,
  IxcPortfolioV1InstantDepositControls,
  IxcRegistrationV1ClearingControls,
  IxmUserauthV1ProgramControls,
  IxmUserauthV1ProgramControlsApiAuth,
} from '@interxfi/types/generated/api';
import { AccordionSummaryGroup } from '../../../../components/card-group/accordion-summary-group';
import type { CardSummaryItem } from '../../../../components/card-group/card-summary-group';
import { getFundingBatchControlsListItems, getFundingControlsListItems } from '../../../funding/source/funding-source-list-items';
import { getPortfolioInstantDepositControlsListItems } from '../../../portfolio/shared-portfolio-list-items';
import { getRegistrationClearingControlsListItems } from '../../../registration/shared-registration-list-items';
import { getProgramApiAuthControlsListItems } from '../program-list-items';
import { programControlSections } from './program-control-sections';
import { ProgramControlSweepsSummary } from './program-control-sweeps-summary';

function getDevelopersItems(controls: IxmUserauthV1ProgramControlsApiAuth | undefined): CardSummaryItem[] {
  const items = getProgramApiAuthControlsListItems(controls);

  return [
    {
      kind: 'permission',
      id: 'canCreateApiKeys',
      item: {
        name: 'Create API Keys',
        value: controls?.settings?.canCreateApiKeys,
        items: [items.limits.apiKeysMax],
      },
    },
    {
      kind: 'permission',
      id: 'canRevokeApiKeys',
      item: {
        name: 'Revoke API Keys',
        value: controls?.settings?.canRevokeApiKeys,
      },
    },
    {
      kind: 'permission',
      id: 'canGetToken',
      item: {
        name: 'Get API Token',
        value: controls?.settings?.canGetToken,
      },
    },
  ];
}

function getBooksItems(controls: IxcPortfolioV1BooksControls | undefined): CardSummaryItem[] {
  return [
    {
      kind: 'permission',
      id: 'canCreditCustomers',
      item: {
        name: 'Credit Customers',
        value: controls?.settings?.canCreditCustomers,
      },
    },
    {
      kind: 'permission',
      id: 'canDebitCustomers',
      item: {
        name: 'Debit Customers',
        value: controls?.settings?.canDebitCustomers,
      },
    },
    {
      kind: 'permission',
      id: 'canTradeUnsettledCash',
      item: {
        name: 'Trade Unsettled Cash',
        value: controls?.settings?.canTradeUnsettledCash,
      },
    },
    {
      kind: 'permission',
      id: 'canCreditPlatformAccount',
      item: {
        name: 'Credit Platform Account',
        value: controls?.settings?.canCreditPlatformAccount,
      },
    },
    {
      kind: 'permission',
      id: 'canDebitPlatformAccounts',
      item: {
        name: 'Debit Platform Accounts',
        value: controls?.settings?.canDebitPlatformAccounts,
      },
    },
  ];
}

function getFundingItems(
  controls: IxcFundingV1FundingControls | undefined,
  batchControls: IxcFundingV1BatchFundingControls | undefined,
): CardSummaryItem[] {
  const items = getFundingControlsListItems(controls);
  const batchItems = getFundingBatchControlsListItems(batchControls);

  return [
    {
      kind: 'permission',
      id: 'achDeposits',
      item: {
        name: 'ACH Pull',
        value: controls?.settings?.ach?.canDeposit,
        items: [
          { ...items.limits.ach.depositPerTransaction.minAmount, title: 'Transaction Min' },
          { ...items.limits.ach.depositPerTransaction.maxAmount, title: 'Transaction Max' },
        ],
      },
    },
    {
      kind: 'permission',
      id: 'achWithdrawals',
      item: {
        name: 'ACH Push',
        value: controls?.settings?.ach?.canWithdraw,
        items: [
          { ...items.limits.ach.withdrawalPerTransaction.minAmount, title: 'Transaction Min' },
          { ...items.limits.ach.withdrawalPerTransaction.maxAmount, title: 'Transaction Max' },
        ],
      },
    },
    {
      kind: 'permission',
      id: 'sameDayAchDeposits',
      item: {
        name: 'Same-Day ACH Pull',
        value: controls?.settings?.sameDayAch?.canDeposit,
        items: [
          { ...items.limits.achSameDay.depositPerTransaction.minAmount, title: 'Transaction Min' },
          { ...items.limits.achSameDay.depositPerTransaction.maxAmount, title: 'Transaction Max' },
        ],
      },
    },
    {
      kind: 'permission',
      id: 'sameDayAchWithdrawals',
      item: {
        name: 'Same-Day ACH Push',
        value: controls?.settings?.sameDayAch?.canWithdraw,
        items: [
          { ...items.limits.achSameDay.withdrawalPerTransaction.minAmount, title: 'Transaction Min' },
          { ...items.limits.achSameDay.withdrawalPerTransaction.maxAmount, title: 'Transaction Max' },
        ],
      },
    },
    {
      kind: 'permission',
      id: 'wireWithdrawals',
      item: {
        name: 'Wire Push',
        value: controls?.settings?.wire?.canWithdraw,
        items: [
          { ...items.limits.wire.withdrawalPerTransaction.minAmount, title: 'Transaction Min' },
          { ...items.limits.wire.withdrawalPerTransaction.maxAmount, title: 'Transaction Max' },
        ],
      },
    },
    {
      kind: 'list',
      id: 'generalLimits',
      items: [items.limits.maxSourcesPerAccount],
    },
    {
      kind: 'permission',
      id: 'canAchBatchDeposit',
      item: {
        name: 'Batch ACH Pull',
        value: batchControls?.settings?.ach?.canDeposit,
      },
    },
    {
      kind: 'permission',
      id: 'canAchBatchWithdraw',
      item: {
        name: 'Batch ACH Push',
        value: batchControls?.settings?.ach?.canWithdraw,
      },
    },
    {
      kind: 'permission',
      id: 'canSameDayAchBatchDeposit',
      item: {
        name: 'Batch Same-Day ACH Pull',
        value: batchControls?.settings?.sameDayAch?.canDeposit,
      },
    },
    {
      kind: 'permission',
      id: 'canSameDayAchBatchWithdraw',
      item: {
        name: 'Batch Same-Day ACH Push',
        value: batchControls?.settings?.sameDayAch?.canWithdraw,
      },
    },
    {
      kind: 'list',
      id: 'generalBatchLimits',
      items: [
        batchItems.limits.perBatchMaximums.deposit,
        batchItems.limits.perBatchMaximums.withdrawal,
        batchItems.limits.autoApproveUnder.deposit,
        batchItems.limits.autoApproveUnder.withdrawal,
      ],
    },
  ];
}

function getOrdersItems(controls: IxcOrdersV1OrderControls | undefined): CardSummaryItem[] {
  return [
    {
      kind: 'permission',
      id: 'usEquities.canPlaceOpening',
      item: {
        name: 'Place Opening Orders',
        value: controls?.settings?.usEquities?.canPlaceOpening,
      },
    },
    {
      kind: 'permission',
      id: 'usEquities.canPlaceClosing',
      item: {
        name: 'Place Closing Orders',
        value: controls?.settings?.usEquities?.canPlaceClosing,
      },
    },
    {
      kind: 'permission',
      id: 'usEquities.canPlaceFractional',
      item: {
        name: 'Place Fractional Orders',
        value: controls?.settings?.usEquities?.canTradeFractions,
      },
    },
    {
      kind: 'permission',
      id: 'usEquities.canChargeCommission',
      item: {
        name: 'Charge Commission',
        value: controls?.settings?.usEquities?.canChargeCommission,
      },
    },
    // {
    //   kind: 'list',
    //   id: 'usEquities.notionalHoldBufferPercent',
    //   items: [items.settings.usEquities.notionalHoldBufferPercent],
    // },
  ];
}

function getBatchOrderItems(controls: IxcOrdersV1BatchOrderControls | undefined): CardSummaryItem[] {
  return [
    {
      kind: 'permission',
      id: 'usEquities.canPlaceBatchOrders',
      item: {
        name: 'Place Batch Orders',
        value: controls?.settings?.usEquities?.canPlaceBatchOrders,
      },
    },
  ];
}

function getRegistrationItems(controls: IxcRegistrationV1ClearingControls | undefined): CardSummaryItem[] {
  const items = getRegistrationClearingControlsListItems(controls);

  return [
    {
      kind: 'permission',
      id: 'canOpenAccounts',
      item: {
        name: 'Open Accounts',
        value: controls?.settings?.canOpenAccounts,
      },
    },
    {
      kind: 'list',
      id: 'settings',
      items: [
        items.settings.accountTypes,
        items.settings.marginType,
        items.settings.statesSummary,
        items.settings.accountReviewType,
        items.settings.specialPurposeAccountReviewType,
        items.settings.cipProgram,
        items.settings.optionalSuitabilityQuestions,
      ],
    },
  ];
}

function getInstantDepositItems(controls: IxcPortfolioV1InstantDepositControls | undefined): CardSummaryItem[] {
  const items = getPortfolioInstantDepositControlsListItems(controls);

  return [
    {
      kind: 'permission',
      id: 'canUseAdvance',
      item: {
        name: 'Allow Advances',
        value: controls?.settings?.canUseAdvance,
        items: [items.limits.maxAdvancePerAccount],
      },
    },
  ];
}

function getAccountNumbersItems(controls: IxcFundingV1AccountNumbersControls | undefined): CardSummaryItem[] {
  return [
    {
      kind: 'permission',
      id: 'canCreate',
      item: {
        name: 'Can Create',
        value: controls?.settings?.canCreate,
      },
    },
  ];
}

interface ProgramControlSummaryProps {
  controls: IxmUserauthV1ProgramControls | undefined;
  defaultOpenSections?: string[];
  isPending: boolean;
  onOpenSectionsChange?: (openSections: string[]) => void;
  openSections?: string[];
  programId: string | undefined;
}

export function ProgramControlSummary({
  controls,
  defaultOpenSections,
  isPending,
  onOpenSectionsChange,
  openSections,
  programId,
}: ProgramControlSummaryProps) {
  const { accountsAndProfiles, batchOrder, books, developers, funding, instantDeposit, orders, accountNumbers } = useMemo(
    () => ({
      accountsAndProfiles: getRegistrationItems(controls?.registration),
      books: getBooksItems(controls?.books),
      developers: getDevelopersItems(controls?.apiAuth),
      funding: getFundingItems(controls?.funding, controls?.batchFunding),
      orders: getOrdersItems(controls?.order),
      instantDeposit: getInstantDepositItems(controls?.instantDeposit),
      batchOrder: getBatchOrderItems(controls?.batchOrder),
      accountNumbers: getAccountNumbersItems(controls?.accountNumbers),
    }),
    [controls],
  );

  return (
    <Accordion
      defaultValue={openSections === undefined ? defaultOpenSections : undefined}
      onValueChange={onOpenSectionsChange}
      type="multiple"
      value={openSections}
    >
      <AccordionSummaryGroup
        icon={<AvatarIcon aria-hidden />}
        id={programControlSections.registration}
        isPending={isPending}
        items={accountsAndProfiles}
        title="Registration"
      />
      <ProgramControlSweepsSummary controls={controls?.sweeps} isPending={isPending} programId={programId} />
      <AccordionSummaryGroup
        icon={<DevelopersIcon aria-hidden />}
        id={programControlSections.developers}
        isPending={isPending}
        items={developers}
        title="Developers"
      />
      <AccordionSummaryGroup
        icon={<BankIcon aria-hidden />}
        id={programControlSections.funding}
        isPending={isPending}
        items={funding}
        title="Funding"
      />
      <AccordionSummaryGroup
        icon={<NumberIcon aria-hidden />}
        id={programControlSections.accountNumbers}
        isPending={isPending}
        items={accountNumbers}
        title="Account Numbers"
      />
      <AccordionSummaryGroup
        icon={<BoltIcon aria-hidden />}
        id={programControlSections.instantDeposit}
        isPending={isPending}
        items={instantDeposit}
        title="Instant Deposit"
      />
      <AccordionSummaryGroup
        icon={<OrdersIcon aria-hidden />}
        id={programControlSections.orders}
        isPending={isPending}
        items={orders}
        title="Orders"
      />
      <AccordionSummaryGroup
        icon={<OrdersIcon aria-hidden />}
        id={programControlSections.batchOrder}
        isPending={isPending}
        items={batchOrder}
        title="Batch Orders"
      />
      <AccordionSummaryGroup icon={<LedgerIcon aria-hidden />} id={programControlSections.books} isPending={isPending} items={books} title="Books" />
    </Accordion>
  );
}
